import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import slugify from 'slugify';
import { TextField, MessageBar, MessageBarType, PrimaryButton, Label, Toggle, Icon } from 'office-ui-fabric-react';
import 'react-sortable-tree/style.css';
import * as _yup from "yup";
import { v4 as uuidv4 } from "uuid";

import { MetaTypeEnum } from 'app/shared';
import { RootState } from "app/redux/reducer";
import { setPageData, setPageErrors, initialPageData } from 'app/redux/page/actions';
import { toggleModal } from 'app/redux/modal/actions';

import PageTitle from 'components/customs/PageTitle';
import { castThunkAction } from 'app/utils/casting';
import { createActivityCommunity, IActivityCommunity, IActivityCommunityRule } from 'app/services/activity-community';
import Sidebar from 'components/customs/Sidebar/Sidebar';
import PageLayout from 'containers/PageLayout';
import FormAccordion from 'components/customs/FormAccordion';
import MetaEditor from 'components/customs/MetaFields/Fields/MetaEditor';
import MetaSingleFile from 'components/customs/MetaFields/Fields/MetaSingleFile';
import { ReactSortable } from 'react-sortablejs';

function mapStateToProps(state: RootState, ownProps: RouteComponentProps) {
    return {
        ...ownProps,
        defaultLanguage: state.system.defaultLanguage,
        language: state.system.language,
        pageData: state.page.pageData as IActivityCommunity,
        pageErrors: state.page.pageErrors,
        contentTypes: state.contentType.contentTypes.list
    }
}

function mapDispatchToProps(dispatch: Dispatch) {
    return {
        ...bindActionCreators({
            setPageData,
            setPageErrors,
            initialPageData,
            createActivityCommunity,
            toggleModal
        }, dispatch)
    };
}

type CreateActivityCommunityProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

interface OwnState {
    messageType: MessageBarType;
    sortableMode: boolean;
    activeAccordion: number;
}

class CreateActivityCommunity extends React.Component<CreateActivityCommunityProps, OwnState> {

    state = {
        messageType: MessageBarType.warning,
        sortableMode: false,
        activeAccordion: -1
    }

    componentDidMount() {
        this.resetData();
    }

    resetData() {
        this.setState({
            messageType: MessageBarType.warning
        }, () => {
            this.props.initialPageData<IActivityCommunity>({
                key: "",
                title: "",
                description: "",
                content: "",
                rules: [],
                order: 30,
                isShowList: false,
                language: this.props.defaultLanguage,
                relationId: "",
            })
        })
    }

    renderTextByMessageType() {
        switch (this.state.messageType) {
            case MessageBarType.success:
                return "Kaydetme işlemi başarıyla gerçekleşmiştir. Düzenleme sayfasına yönlendiriliyorsunuz..."
            case MessageBarType.error:
                return "Kayıt işlemi yapılırken bir hata oluşmuştur. Lütfen servis sağlayıcınızla iletişime geçin."
            default:
                return null;
        }
    }

    renderMessageBar() {
        if (this.state.messageType !== MessageBarType.warning)
            return (
                <MessageBar
                    messageBarType={this.state.messageType}
                    isMultiline={false}
                >
                    {this.renderTextByMessageType()}
                </MessageBar>
            )
        return null;
    }

    /**
     * Validation Şemasını Kontrol Ederek, Eğer Validate ise Doğrulama modalını aktive eder, Değilse İlgili Yerlere Hata Dönecek Yani,
     * formErrors Objesini Dolduracak Action'u Tetikler.
     */
    onSubmit() {
        const {
            pageData,
            setPageErrors
        } = this.props;
        const validationSchema = _yup.object({
            title: _yup.string().required(
                "Bu alan zorunludur!"
            )
        });
        let formErrors = {};
        validationSchema
            .validate(pageData, {
                abortEarly: false
            })
            .then(() => {
                setPageErrors({});
                castThunkAction<IActivityCommunity>(this.props.createActivityCommunity(this.props.pageData)).then(res => {
                    this.setState({
                        messageType: res?.id ? MessageBarType.success : MessageBarType.error
                    }, () => {
                        if (res?.id) {
                            setTimeout(() => {
                                this.props.history.push(`/activity-community/edit/${res.id}`)
                            }, 1200)
                        }
                    })

                })
            })
            .catch(err => {
                err.inner.forEach((e: any) => {
                    const { path, message } = e;
                    formErrors[path] = message;
                });
                setPageErrors(formErrors);
            });
    };

    renderForm() {
        return (
            <div className="form-groups row">
                <div className="form-group col-md-12">
                    <FormAccordion showContent={true} title="Genel Bilgiler">
                        <div className="form-wrapper row" style={{ padding: "10px 5px" }}>
                            <div className="item row col-md-6">
                                <TextField
                                    className="custom-textfield"
                                    label="Etkinlik Topluluk Adı"
                                    value={this.props.pageData.title}
                                    onChange={(event: any) => {
                                        this.props.initialPageData({
                                            ...this.props.pageData,
                                            title: event.target.value,
                                            key: slugify(event.target.value, { lower: true, replacement: '-', strict: true })
                                        })
                                    }}
                                />
                            </div>
                            <div className="item row col-md-6">
                                <TextField
                                    className="custom-textfield"
                                    label="Etkinlik Topluluk Anahtarı"
                                    value={this.props.pageData.key}
                                    disabled
                                />
                            </div>
                            <div className="item row col-md-12">
                                <TextField
                                    className="custom-textfield"
                                    label="Etkinlik Topluluk Açıklaması"
                                    value={this.props.pageData.description}
                                    onChange={(event: any) => {
                                        this.props.setPageData("description", event.target.value)
                                    }}
                                    multiline={true}
                                    resizable={false}
                                />
                            </div>
                            <div className="item row col-md-12">
                                <Label>Etkinlik Topluluk İçeriği</Label>
                                <MetaEditor
                                    type={MetaTypeEnum.Editor}
                                    onChange={(value: string) => {
                                        this.props.setPageData("content", value)
                                    }}
                                    meta={{ data: this.props.pageData.content, type: MetaTypeEnum.Editor, title: '', column: 12, name: '' }}
                                    toggleModal={toggleModal}
                                />
                            </div>
                            <div className="item row col-md-12" style={{ paddingTop: 8 }}>
                                <Toggle
                                    className="custom-toggle m-left-auto"
                                    onChange={(_, checked) => this.props.setPageData("isShowList", checked)}
                                    checked={this.props.pageData.isShowList}
                                    label="Etkinlik Topluluk Altında Etkinlik Olmasa Dahi Listelerde Gözüksün Mü?"
                                />
                            </div>
                        </div>
                    </FormAccordion>
                </div>
            </div>
        )
    }

    renderRuleItems() {
        return (
            (this.props.pageData.rules || []).map((rule: IActivityCommunityRule, index: number) => (
                <div
                    key={index}
                    className={
                        this.state.activeAccordion ===
                            index
                            ? "fa-Accordion-item active"
                            : "fa-Accordion-item"
                    }
                >
                    <div className="title row">
                        <div
                            onClick={() => {
                                this.setState({ activeAccordion: this.state.activeAccordion !== index ? index : -1 })
                            }}
                            className="text col no-padding"
                        >
                            {rule.title || `Kural ${index + 1}`}
                        </div>
                        <Icon
                            onClick={() => {
                                const rules = [...this.props.pageData.rules]

                                rules.splice(index, 1);
                                this.props.setPageData("rules", rules);
                            }}
                            className="m-left-auto remove-accordion"
                            iconName="ChromeClose"
                        />
                    </div>
                    {this.state.activeAccordion === index && (
                        <div className="content">
                            <div className="item row col-md-12">
                                <TextField
                                    className="custom-textfield"
                                    label="Başlık"
                                    value={rule.title}
                                    onChange={(event: any) => {
                                        const rules = [...this.props.pageData.rules]

                                        rules[index].title = event.target.value
                                        this.props.setPageData("rules", rules);
                                    }}
                                />
                            </div>
                            <div className="item row col-md-12">
                                <Label>İçerik</Label>
                                <MetaEditor
                                    type={MetaTypeEnum.Editor}
                                    onChange={(value: string) => {
                                        const rules = [...this.props.pageData.rules]

                                        rules[index].content = value
                                        this.props.setPageData("rules", rules);
                                    }}
                                    meta={{ data: rule.content, type: MetaTypeEnum.Editor, title: '', column: 12, name: '' }}
                                    toggleModal={toggleModal}
                                />
                            </div>
                        </div>
                    )}
                </div>
            ))
        )
    }

    renderRules() {
        return (
            <div className="form-groups row">
                <div className="form-group col-md-12" style={{ paddingTop: 0 }}>
                    <FormAccordion showContent={true} title="Etkinlik Kural Bilgileri">
                        <div className="form-wrapper row" style={{ padding: "15px 20px" }}>
                            <div className="accordion-wrapper-header">
                                <div className="accordion-wrapper-header-title">Kurallar</div>
                                <PrimaryButton
                                    iconProps={{ iconName: "CalculatorAddition" }}
                                    className="custom-button"
                                    text="Yeni Ekle"
                                    onClick={() => {
                                        this.props.setPageData("rules", [...this.props.pageData.rules, { id: uuidv4(), title: '', content: '' }]);
                                    }}
                                    styles={{
                                        root: {
                                            fontSize: 11,
                                            background: "#33926e !important",
                                            borderColor: "#33926e !important"
                                        }
                                    }}
                                />
                                <Toggle
                                    className="custom-toggle m-left-auto"
                                    onChange={(_, checked) => this.setState({ sortableMode: checked as boolean })}
                                    checked={this.state.sortableMode}
                                    label="Sıralama Modu"
                                    inlineLabel
                                    styles={{
                                        root: {
                                            marginBottom: "-5px",
                                            marginTop: "-1px !important"
                                        },
                                        container: {
                                            marginTop: "-1px !important"
                                        }
                                    }}
                                />
                            </div>
                            <div className="fa-Accordion accordion-wrapper-content">
                                {this.state.sortableMode ? (
                                    <ReactSortable
                                        list={this.props.pageData.rules || []}
                                        setList={(rules: IActivityCommunityRule[]) => this.props.setPageData("rules", rules)}
                                        animation={150}
                                    >
                                        {this.renderRuleItems()}
                                    </ReactSortable>
                                ) : this.renderRuleItems()}
                            </div>
                        </div>
                    </FormAccordion>
                </div>
            </div>
        )
    }

    renderImages() {
        return (
            <div className="form-groups row">
                <div className="form-group col-md-12" style={{ paddingTop: 0 }}>
                    <FormAccordion showContent={true} title="Görsel Ayarları">
                        <div className="form-wrapper row" style={{ padding: "10px 5px" }}>
                            <div className="item row col-md-6">
                                <Label>Yatay Görsel</Label>
                                <MetaSingleFile
                                    type={MetaTypeEnum.SingleImage}
                                    onChange={(value) => {
                                        this.props.setPageData("profileImageHorizontal", value)
                                    }}
                                    meta={{ data: this.props.pageData.profileImageHorizontal, type: MetaTypeEnum.SingleImage, title: '', column: 12, name: '' }}
                                    toggleModal={toggleModal}
                                />
                            </div>
                            <div className="item row col-md-6">
                                <Label>Dikey Görsel</Label>
                                <MetaSingleFile
                                    type={MetaTypeEnum.SingleImage}
                                    onChange={(value) => {
                                        this.props.setPageData("profileImageVertical", value)
                                    }}
                                    meta={{ data: this.props.pageData.profileImageVertical, type: MetaTypeEnum.SingleImage, title: '', column: 12, name: '' }}
                                    toggleModal={toggleModal}
                                />
                            </div>
                        </div>
                    </FormAccordion>
                </div>
            </div>
        )
    }

    render() {
        return (
            <PageLayout history={this.props.history} match={this.props.match} location={this.props.location}>
                <div className="page push-all">
                    <PageTitle
                        title="Yeni Etkinlik Topluluk Ekle"
                        titleRenderer={
                            () => (
                                <span className="edit-title">
                                    Yönet:
                                    <strong>Yeni Etkinlik Topluluk Ekle</strong>
                                </span>
                            )
                        }
                        showBack
                        backUrl="/activity-communities"
                        history={this.props.history}
                        subTitle="Yeni bir etkinlik topluluğu eklemek için aşağıdaki bilgileri doldurunuz, dilerseniz taslak olarakta oluşturabilirsiniz."
                    />
                    <PrimaryButton
                        onClick={() => { this.resetData() }}
                        iconProps={{ iconName: "Add" }}
                        className="absolute-create"
                        text="Yeni Etkinlik Topluluk Ekle"
                        styles={{
                            root: {
                                background: "#62a98e",
                                borderColor: "#5b9e85",
                                color: "#fff"
                            },
                            rootHovered: {
                                background: "#5b9e85",
                                borderColor: "#5b9e85"
                            },
                            rootDisabled: {
                                background: "#ececec"
                            }
                        }}
                    />
                    <div className="content row">
                        <div className="left col no-padding">
                            {this.renderMessageBar()}
                            {this.renderForm()}
                            {this.renderRules()}
                            {this.renderImages()}
                        </div>
                        <Sidebar
                            categoryBar={{
                                hide: true
                            }}
                            publishBar={{
                                onSave: () => this.onSubmit(),
                                disabledSaveButton: this.state.messageType === MessageBarType.success ? true : false,
                                hideDeleteButton: true,
                                hideDraftButton: true,
                                hidePreviewButton: true,
                                showOrder: true,
                                order: this.props.pageData?.order || 0,
                                onChangeOrderIndex: (order) => this.props.setPageData("order", order),
                            }}
                            layoutBar={{
                                hide: true
                            }}
                            archiveBar={{
                                hide: true
                            }}
                            thumbnailBar={{
                                hide: true
                            }}
                        />
                    </div>
                </div>
            </PageLayout>
        )
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateActivityCommunity);